"use client";

import type { JSX } from "react";
import dynamic from "next/dynamic";
import { useState } from "react";
import { trackCalculatorUse } from "@/lib/analytics";

const MacroDoughnutChart = dynamic(() => import("../components/MacroDoughnutChart"), {
  ssr: false,
  loading: () => (
    <div
      className="h-64 w-64 animate-pulse rounded-full bg-gray-100"
      aria-label="Loading meal plan chart"
      role="status"
    />
  ),
});

interface MealPlan {
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFat: number;
  mealsPerDay: number;
  meals: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  }[];
}

interface ValidationErrors {
  calories?: string;
  protein?: string;
  carbs?: string;
  fat?: string;
}

export default function MealPlannerClient(): JSX.Element {
  const [formData, setFormData] = useState({
    calories: "",
    protein: "",
    carbs: "",
    fat: "",
    mealsPerDay: "4",
  });
  const [mealPlan, setMealPlan] = useState<MealPlan | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Calories validation
    if (!formData.calories) {
      newErrors.calories = "This field is required";
    } else {
      const calories = Number(formData.calories);
      if (calories < 500) {
        newErrors.calories = "Minimum 500 calories required";
      } else if (calories > 10000) {
        newErrors.calories = "Maximum 10,000 calories allowed";
      }
    }

    // Protein validation
    if (!formData.protein) {
      newErrors.protein = "This field is required";
    } else {
      const protein = Number(formData.protein);
      if (protein < 0) {
        newErrors.protein = "Minimum 0g required";
      } else if (protein > 1000) {
        newErrors.protein = "Maximum 1,000g allowed";
      }
    }

    // Carbs validation
    if (!formData.carbs) {
      newErrors.carbs = "This field is required";
    } else {
      const carbs = Number(formData.carbs);
      if (carbs < 0) {
        newErrors.carbs = "Minimum 0g required";
      } else if (carbs > 1000) {
        newErrors.carbs = "Maximum 1,000g allowed";
      }
    }

    // Fat validation
    if (!formData.fat) {
      newErrors.fat = "This field is required";
    } else {
      const fat = Number(formData.fat);
      if (fat < 0) {
        newErrors.fat = "Minimum 0g required";
      } else if (fat > 1000) {
        newErrors.fat = "Maximum 1,000g allowed";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateMealPlan = (): void => {
    if (!validateForm()) {
      return;
    }

    const totalCalories = Number(formData.calories);
    const totalProtein = Number(formData.protein);
    const totalCarbs = Number(formData.carbs);
    const totalFat = Number(formData.fat);
    const mealsPerDay = Number(formData.mealsPerDay);

    // Helper function to distribute values with correct rounding
    const distributeValue = (total: number, numMeals: number): number[] => {
      const baseAmount = Math.floor(total / numMeals);
      const remainder = total - (baseAmount * numMeals);

      return Array.from({ length: numMeals }, (_, index) =>
        baseAmount + (index < remainder ? 1 : 0)
      );
    };

    // Distribute macros evenly with correct rounding
    const caloriesPerMeal = distributeValue(totalCalories, mealsPerDay);
    const proteinPerMeal = distributeValue(totalProtein, mealsPerDay);
    const carbsPerMeal = distributeValue(totalCarbs, mealsPerDay);
    const fatPerMeal = distributeValue(totalFat, mealsPerDay);

    // Create meal plan with distributed values
    const meals = Array.from({ length: mealsPerDay }, (_, index) => ({
      calories: caloriesPerMeal[index],
      protein: proteinPerMeal[index],
      carbs: carbsPerMeal[index],
      fat: fatPerMeal[index],
    }));

    setMealPlan({
      totalCalories,
      totalProtein,
      totalCarbs,
      totalFat,
      mealsPerDay,
      meals,
    });

    trackCalculatorUse("meal_planner", {
      calories: totalCalories,
      protein: totalProtein,
      carbs: totalCarbs,
      fat: totalFat,
      meals_per_day: mealsPerDay,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const isFormValid = (): boolean => {
    return !!(
      formData.calories &&
      formData.protein &&
      formData.carbs &&
      formData.fat
    );
  };

  const chartData = mealPlan
    ? {
        labels: ["Protein", "Carbs", "Fat"],
        datasets: [
          {
            data: [
              ((mealPlan.totalProtein * 4) / mealPlan.totalCalories) * 100,
              ((mealPlan.totalCarbs * 4) / mealPlan.totalCalories) * 100,
              ((mealPlan.totalFat * 9) / mealPlan.totalCalories) * 100,
            ],
            backgroundColor: ["#4F46E5", "#10B981", "#F59E0B"],
            borderWidth: 0,
          },
        ],
      }
    : null;

  return (
    <div className="bg-white py-12 sm:py-18">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <h1 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
            Meal Planner
          </h1>
          <p className="mt-2 text-lg leading-8 text-gray-600">
            Plan your daily meals based on your macro goals
          </p>

          <div className="mt-12 space-y-8">
            {/* Daily Targets Inputs */}
            <div className="space-y-2">
              <label
                htmlFor="calories"
                className="block text-sm font-medium text-gray-700"
              >
                Total Daily Calories
              </label>
              <input
                type="number"
                id="calories"
                name="calories"
                value={formData.calories}
                onChange={handleInputChange}
                className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                  errors.calories ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.calories && (
                <p className="mt-1 text-sm text-red-600">{errors.calories}</p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="protein"
                className="block text-sm font-medium text-gray-700"
              >
                Total Protein (g)
              </label>
              <input
                type="number"
                id="protein"
                name="protein"
                value={formData.protein}
                onChange={handleInputChange}
                className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                  errors.protein ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.protein && (
                <p className="mt-1 text-sm text-red-600">{errors.protein}</p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="carbs"
                className="block text-sm font-medium text-gray-700"
              >
                Total Carbs (g)
              </label>
              <input
                type="number"
                id="carbs"
                name="carbs"
                value={formData.carbs}
                onChange={handleInputChange}
                className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                  errors.carbs ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.carbs && (
                <p className="mt-1 text-sm text-red-600">{errors.carbs}</p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="fat"
                className="block text-sm font-medium text-gray-700"
              >
                Total Fat (g)
              </label>
              <input
                type="number"
                id="fat"
                name="fat"
                value={formData.fat}
                onChange={handleInputChange}
                className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                  errors.fat ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.fat && (
                <p className="mt-1 text-sm text-red-600">{errors.fat}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Meals Per Day
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[3, 4, 5, 6].map((meals) => (
                  <button
                    key={meals}
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        mealsPerDay: meals.toString(),
                      })
                    }
                    className={`py-2 px-4 rounded-lg border ${
                      formData.mealsPerDay === meals.toString()
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {meals}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-center pt-6">
              <button
                onClick={calculateMealPlan}
                disabled={!isFormValid()}
                className={`rounded-lg px-8 py-3 text-base font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors duration-200 ${
                  isFormValid()
                    ? "bg-indigo-600 hover:bg-indigo-500 cursor-pointer"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Generate Meal Plan
              </button>
            </div>
          </div>

          {mealPlan && (
            <div className="mt-12 space-y-12">
              {/* Daily Totals */}
              <div className="rounded-lg bg-gray-50 p-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Daily Totals
                </h3>
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="rounded-lg bg-white p-4 text-center shadow">
                    <div className="text-sm text-gray-500">Calories</div>
                    <div className="mt-1 text-xl font-semibold text-gray-900">
                      {mealPlan.totalCalories}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-4 text-center shadow">
                    <div className="text-sm text-gray-500">Protein</div>
                    <div className="mt-1 text-xl font-semibold text-gray-900">
                      {mealPlan.totalProtein}g
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-4 text-center shadow">
                    <div className="text-sm text-gray-500">Carbs</div>
                    <div className="mt-1 text-xl font-semibold text-gray-900">
                      {mealPlan.totalCarbs}g
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-4 text-center shadow">
                    <div className="text-sm text-gray-500">Fat</div>
                    <div className="mt-1 text-xl font-semibold text-gray-900">
                      {mealPlan.totalFat}g
                    </div>
                  </div>
                </div>

                {/* Macro Distribution Chart */}
                <div className="mt-8 flex justify-center">
                  <div className="w-64">
                    {chartData && (
                      <MacroDoughnutChart
                        data={chartData}
                        options={{
                          plugins: {
                            legend: {
                              position: "bottom",
                            },
                            tooltip: {
                              callbacks: {
                                label: function (context) {
                                  return `${context.label}: ${Math.round(
                                    context.raw as number
                                  )}%`;
                                },
                              },
                            },
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Meal Breakdown */}
              <div className="rounded-lg bg-gray-50 p-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Meal Breakdown
                </h3>
                <div className="mt-4 space-y-4">
                  {mealPlan.meals.map((meal, index) => (
                    <div key={index} className="rounded-lg bg-white p-4 shadow">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900">
                          Meal {index + 1}
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div className="text-center">
                            <div className="text-gray-500">Cal</div>
                            <div className="font-semibold">{meal.calories}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-500">P</div>
                            <div className="font-semibold">{meal.protein}g</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-500">C</div>
                            <div className="font-semibold">{meal.carbs}g</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-500">F</div>
                            <div className="font-semibold">{meal.fat}g</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
